import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import KopSurat from "@/components/KopSurat";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { getVisitor } from "@/lib/api";
import { formatHariTanggalID } from "@/lib/format";
import { useSettings } from "@/lib/useSettings";

export default function PrintAbsensi() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const settings = useSettings();

  useEffect(() => { getVisitor(id).then(setData); }, [id]);
  if (!data) return <div className="p-10 text-center text-slate-500">Memuat dokumen…</div>;

  const names = [
    { nama: data.nama_tamu, jabatan: data.jabatan_tamu, role: "Ketua Rombongan" },
  ];
  // Prefer structured anggota_tamu array
  (data.anggota_tamu || []).forEach((a) => {
    if (a?.nama) names.push({ nama: a.nama, jabatan: a.jabatan || data.jabatan_tamu || "-", role: "Anggota Tamu" });
  });
  // Prefer structured daftar_pendamping, fallback to legacy nama_pendamping text
  const pdList = (data.daftar_pendamping || []).filter(p => p?.nama);
  if (pdList.length > 0) {
    pdList.forEach(p => names.push({ nama: p.nama, jabatan: p.jabatan || data.jabatan_pendamping || "-", role: "Pendamping" }));
  } else {
    (data.nama_pendamping || "").split(/[\n,]+/).map(s => s.trim()).filter(Boolean)
      .forEach(nm => names.push({ nama: nm, jabatan: data.jabatan_pendamping || "-", role: "Pendamping" }));
  }
  // pad to min 10 rows for signature space
  while (names.length < 10) names.push({ nama: "", jabatan: "", role: "" });

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="no-print max-w-4xl mx-auto px-4 mb-4 flex justify-between items-center">
        <h2 className="text-lg font-semibold text-slate-800">Pratinjau Daftar Hadir / Absensi</h2>
        <div className="flex gap-2">
          <Button onClick={() => window.print()} className="btn-primary" data-testid="print-trigger-absensi">
            <Printer className="w-4 h-4 mr-2" /> Cetak / Simpan PDF
          </Button>
          <Button variant="outline" onClick={() => window.close()}>Tutup</Button>
        </div>
      </div>

      <div className="print-area max-w-4xl mx-auto bg-white p-10 md:p-14 shadow-xl print:shadow-none print:p-0">
        <KopSurat />

        <div className="mt-8 text-center">
          <div className="text-lg md:text-xl font-bold uppercase tracking-widest kop-surat">
            Daftar Hadir Tamu Kunjungan
          </div>
          <div className="text-sm kop-surat mt-1">
            Nomor Surat Kunjungan: {data.nomor_surat_kunjungan} · Nomor Surat Tugas: {data.nomor_surat_tugas}
          </div>
        </div>

        <div className="mt-6 kop-surat text-sm">
          <table className="w-full">
            <tbody>
              <tr><td className="w-44 py-1">Hari / Tanggal</td><td className="py-1">: {formatHariTanggalID(data.tanggal_kunjungan)}</td></tr>
              <tr><td className="py-1">Waktu</td><td className="py-1">: {data.waktu_kunjungan} WIB</td></tr>
              <tr><td className="py-1">Instansi Asal</td><td className="py-1">: {data.instansi_asal} — {data.daerah_asal}</td></tr>
              <tr><td className="py-1">Perihal</td><td className="py-1">: {data.perihal_kunjungan}</td></tr>
              <tr><td className="py-1">Agenda</td><td className="py-1">: {data.rencana_kunjungan}</td></tr>
            </tbody>
          </table>
        </div>

        <table className="w-full mt-6 border-collapse border border-black kop-surat text-sm">
          <thead>
            <tr className="bg-slate-100">
              <th className="border border-black py-2 w-10">No.</th>
              <th className="border border-black py-2">Nama Lengkap</th>
              <th className="border border-black py-2">Jabatan</th>
              <th className="border border-black py-2 w-32">Keterangan</th>
              <th className="border border-black py-2 w-40">Tanda Tangan</th>
            </tr>
          </thead>
          <tbody>
            {names.map((n, i) => (
              <tr key={i}>
                <td className="border border-black py-2 text-center">{i + 1}</td>
                <td className="border border-black py-2 px-2">{n.nama}</td>
                <td className="border border-black py-2 px-2">{n.jabatan}</td>
                <td className="border border-black py-2 px-2">{n.role}</td>
                <td className="border border-black py-6 text-center">{n.nama ? `${i + 1}.` : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-10 grid grid-cols-2 gap-10 kop-surat text-sm">
          <div>
            <div>Penanggung Jawab Kegiatan,</div>
            <div className="h-20" />
            <div className="font-bold underline">{data.penanggung_jawab}</div>
          </div>
          <div className="text-right">
            <div>{settings?.kota_ttd || "Kuala Pembuang"}, {formatHariTanggalID(data.tanggal_kunjungan)}</div>
            <div>{settings?.jabatan_penandatangan || "Sekretaris DPRD Kabupaten Seruyan"},</div>
            <div className="h-20" />
            <div className="font-bold underline">{settings?.nama_penandatangan || "(..............................)"}</div>
            <div>NIP. {settings?.nip_penandatangan || ".............................."}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
